// z10–z12 (Diego, 14/16-oct-2026): cliente NUEVO de casa o negocio, sin recibos. «Que en un texto le puedas escribir todos
// los equipos y describir todo lo que va a tener, y que con IA salga el estimado». La IA es la de z25 («corregir», el modelo
// de Configuración → IA).
// z58 (Diego, 28-oct-2026): «un botón o sección que diga CALCULAR CONSUMOS CON IA, y después poder modificarlos o ajustarlos y
// ya AUTORIZAR eso para continuar haciendo la propuesta». También en híbrido y aislado. La tabla de 12 meses se edita (un mes
// o el promedio) y es EXACTAMENTE lo que cotiza el motor (logica/consumos-ia.ts); «Usar estos consumos» la autoriza y sigue.
import { useState, type KeyboardEvent } from 'react';
import { Check, Sparkles, Trash2 } from 'lucide-react';
import { DIAS_MES_CUADRO_CARGAS, MAX_DESCRIPCION, PREFIJO_FUENTE_DESCRIPCION, kwhMesDeCarga, numero } from '@cotizador/compartido';
import type { TipoProyecto } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { Boton, CampoArea } from '../../componentes/ui.tsx';
import type { FormaCarga } from '../../logica/cuestionario.ts';
import { MESES_TABLA, ajustarMes, ajustarPromedio, cuadroConCurva, mesesDeCargas } from '../../logica/consumos-ia.ts';

const num = (s: string) => {
  const x = Number(String(s).replace(/,/g, '').trim());
  return Number.isFinite(x) && x >= 0 ? x : 0;
};
/** Lo que escribió el vendedor, si la fuente salió de una descripción. */
export const descripcionDeFuente = (fuente: string) => (fuente.startsWith(PREFIJO_FUENTE_DESCRIPCION) ? fuente.slice(PREFIJO_FUENTE_DESCRIPCION.length) : '');

export function EstimarConIA({
  tipo,
  tipoProyecto,
  cargas,
  fuente,
  error,
  sugerencia = '',
  minimoKwhMes = null,
  autorizado,
  alCambiar,
  alUsar,
}: {
  /** Para la IA: casa o negocio. */
  tipo: 'casa' | 'comercio';
  /** Para el reparto mes a mes (el del motor): con curva propia (casa/negocio) o con el promedio (baterías, aislado). */
  tipoProyecto: TipoProyecto | null;
  cargas: FormaCarga[];
  fuente: string;
  error: string | null;
  /** Aislado: la descripción que se arma con los equipos de arriba (si el vendedor no escribe otra). */
  sugerencia?: string;
  /** Aislado: con menos kWh al mes que sus equipos, los paneles no alcanzan. */
  minimoKwhMes?: number | null;
  autorizado: boolean;
  /** Cargas nuevas y la fuente de la estimación (la descripción, para el historial y el PDF). */
  alCambiar: (cargas: FormaCarga[], fuente?: string) => void;
  /** «Usar estos consumos»: autoriza y sigue al siguiente paso. */
  alUsar: () => void;
}) {
  const [texto, setTexto] = useState(() => descripcionDeFuente(fuente));
  const [motivos, setMotivos] = useState<string[]>([]);
  const [supuestos, setSupuestos] = useState<string[]>([]);
  const [estimando, setEstimando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  // Lo que se está tecleando en un mes o en el promedio: se aplica al salir del campo (o con Enter), no a cada tecla.
  const [editando, setEditando] = useState<{ campo: string; valor: string } | null>(null);
  const casa = tipo === 'casa';
  const descripcion = (texto.trim() || sugerencia.trim()).slice(0, MAX_DESCRIPCION);

  async function calcular() {
    setEstimando(true);
    setFalla(null);
    try {
      const r = await api.estimarConsumo(descripcion, tipo);
      setMotivos(r.motivos);
      setSupuestos(r.supuestos);
      alCambiar(
        r.cargas.map((c) => ({ equipo: c.equipo, cantidad: String(c.cantidad), w: String(c.w), horas_dia: String(c.horas_dia), meses_uso: String(c.meses_uso) })),
        `${PREFIJO_FUENTE_DESCRIPCION}${descripcion}`.slice(0, 300),
      );
    } catch (e) {
      setFalla(e instanceof Error ? e.message : String(e));
    } finally {
      setEstimando(false);
    }
  }

  const cambiar = (i: number, k: keyof FormaCarga, v: string) => alCambiar(cargas.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  // Corregir los kWh al mes de un renglón: se ajustan sus watts (cantidad, horas y meses se quedan como están).
  const cambiarKwh = (i: number, v: string) => {
    const c = cargas[i]!;
    const base = num(c.cantidad) * num(c.horas_dia) * DIAS_MES_CUADRO_CARGAS;
    if (!(base > 0) || !(num(v) > 0)) return;
    cambiar(i, 'w', String(Math.round(((num(v) * 1000) / base) * 10) / 10));
  };
  const quitar = (i: number) => {
    setMotivos((m) => m.filter((_, j) => j !== i));
    alCambiar(cargas.filter((_, j) => j !== i));
  };
  const porMes = mesesDeCargas(tipoProyecto, cargas);
  const anual = porMes.reduce((a, b) => a + b, 0);
  const promedio = anual / 12;
  const alto = Math.max(1, ...porMes);
  const conCurva = cuadroConCurva(tipoProyecto);

  // Lo que se teclea se guarda aparte y se aplica al salir del campo (o con Enter). Mientras no se teclee nada, el campo
  // enseña el valor vivo (verificación propia: si se tomaba al enfocar, un mes enfocado se quedaba con el número de antes).
  const valorDe = (campo: string, x: number) => (editando?.campo === campo ? editando.valor : String(Math.round(x)));
  function aplicar(campo: string, actual: number) {
    if (!editando || editando.campo !== campo) return;
    const v = num(editando.valor);
    setEditando(null);
    // salir sin cambiar el número no mueve nada (ni quita la autorización)
    if (!(v > 0) || v === Math.round(actual)) return;
    if (campo === 'promedio') alCambiar(ajustarPromedio(tipoProyecto, cargas, v));
    else alCambiar(ajustarMes(tipoProyecto, cargas, Number(campo), v));
  }
  const entrada = (campo: string, x: number, etiqueta: string, className: string) => ({
    className,
    inputMode: 'numeric' as const,
    'aria-label': etiqueta,
    value: valorDe(campo, x),
    onChange: (e: { target: { value: string } }) => setEditando({ campo, valor: e.target.value }),
    onBlur: () => aplicar(campo, x),
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') e.currentTarget.blur();
    },
  });

  return (
    <section className="estimar-ia" aria-label="Consumos con IA">
      <CampoArea
        etiqueta={casa ? 'Describe la casa y sus equipos' : 'Describe el negocio y sus equipos'}
        rows={3}
        maxLength={MAX_DESCRIPCION}
        placeholder={
          sugerencia
            ? sugerencia
            : casa
              ? 'Ej. casa de dos pisos, 10 toneladas de aire, 4 recámaras, alberca con bomba, carro eléctrico'
              : 'Ej. tienda de abarrotes de 120 m2, 2 minisplits, 3 refrigeradores, 2 computadoras'
        }
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        ayuda={
          sugerencia
            ? 'Si lo dejas vacío, la IA usa los equipos de arriba. Agrega lo que falte: personas, aires, refrigeradores…'
            : 'Todo lo que va a tener: aires (toneladas), recámaras, personas, alberca, carro eléctrico, estufa o calentador eléctrico…'
        }
      />
      <div className="fila-botones fila-botones--entre">
        <Boton variante={cargas.length ? 'secundario' : 'principal'} icono={<Sparkles aria-hidden />} onClick={() => void calcular()} disabled={descripcion.length < 3 || estimando}>
          {estimando ? 'Calculando…' : cargas.length ? 'Volver a calcular con IA' : 'Calcular consumos con IA'}
        </Boton>
      </div>
      {falla && (
        <p className="campo__error" role="alert">
          {falla}
        </p>
      )}

      {cargas.length > 0 && (
        <>
          <table className="estimar-ia__meses" aria-label="Consumo estimado mes por mes (kWh)">
            <thead>
              <tr>
                {MESES_TABLA.map((m) => (
                  <th key={m} scope="col">
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* barras y campos en renglones aparte: una barra alta ya no empuja su campo hacia abajo */}
              <tr className="estimar-ia__barras" aria-hidden>
                {porMes.map((k, i) => (
                  <td key={MESES_TABLA[i]}>
                    <span className="estimar-ia__barra" style={{ blockSize: `${Math.round((k / alto) * 100)}%` }} />
                  </td>
                ))}
              </tr>
              <tr>
                {porMes.map((k, i) => (
                  <td key={MESES_TABLA[i]}>
                    <input {...entrada(String(i), k, `kWh de ${MESES_TABLA[i]}`, 'estimar-ia__entrada estimar-ia__mes')} />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="estimar-ia__resumen">
            <label className="estimar-ia__promedio">
              <span>Promedio al mes</span>
              <input {...entrada('promedio', promedio, 'Promedio al mes (kWh)', 'estimar-ia__entrada')} />
              <span>kWh</span>
            </label>
            <span>
              Total al año: <strong>{numero(anual)} kWh</strong>
            </span>
          </div>
          <p className="nota-suave">
            De dónde sale: la IA supuso los equipos de la descripción y el consumo se reparte con la curva de calor de casas de Sonora
            {conCurva ? ' (lo de todo el año va parejo; los aires siguen la curva)' : ''}. Cambia un mes o el promedio: los demás meses se acomodan con la curva.
          </p>
          {minimoKwhMes !== null && minimoKwhMes > 0 && promedio < minimoKwhMes && (
            <div className="fila-botones fila-botones--entre">
              <span className="texto-rojo">Sus equipos usan unos {numero(minimoKwhMes)} kWh al mes; con menos, los paneles no alcanzan y el PDF no sale.</span>
              <Boton chico onClick={() => alCambiar(ajustarPromedio(tipoProyecto, cargas, minimoKwhMes))}>{`Subir a ${numero(minimoKwhMes)} kWh al mes`}</Boton>
            </div>
          )}

          <details className="estimar-ia__supuestos">
            <summary>Equipos que supuso la IA ({cargas.length}): corrígelos si el cliente te dio otro dato</summary>
            <table className="estimar-ia__tabla">
              <thead>
                <tr>
                  <th scope="col">Equipo</th>
                  <th scope="col">Cuántos</th>
                  <th scope="col">Horas/día</th>
                  <th scope="col">Meses/año</th>
                  <th scope="col">kWh/mes</th>
                  <th scope="col">
                    <span className="solo-lector">Quitar</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {cargas.map((c, i) => (
                  <tr key={i}>
                    <th scope="row">
                      {c.equipo}
                      {motivos[i] && <span className="estimar-ia__motivo">{motivos[i]}</span>}
                    </th>
                    <td>
                      <input className="estimar-ia__entrada" inputMode="numeric" aria-label={`Cuántos: ${c.equipo}`} value={c.cantidad} onChange={(e) => cambiar(i, 'cantidad', e.target.value)} />
                    </td>
                    <td>
                      <input className="estimar-ia__entrada" inputMode="decimal" aria-label={`Horas al día: ${c.equipo}`} value={c.horas_dia} onChange={(e) => cambiar(i, 'horas_dia', e.target.value)} />
                    </td>
                    <td>
                      <input className="estimar-ia__entrada" inputMode="numeric" aria-label={`Meses al año: ${c.equipo}`} value={c.meses_uso} onChange={(e) => cambiar(i, 'meses_uso', e.target.value)} />
                    </td>
                    <td>
                      <input
                        className="estimar-ia__entrada"
                        inputMode="numeric"
                        aria-label={`kWh al mes: ${c.equipo}`}
                        value={String(Math.round(kwhMesDeCarga({ cantidad: num(c.cantidad), w: num(c.w), horas_dia: num(c.horas_dia) })))}
                        onChange={(e) => cambiarKwh(i, e.target.value)}
                      />
                    </td>
                    <td>
                      <button type="button" className="icono-boton" aria-label={`Quitar ${c.equipo}`} onClick={() => quitar(i)}>
                        <Trash2 aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {supuestos.length > 0 && (
              <ul className="ajuste-consumos__supuestos">
                {supuestos.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            )}
          </details>

          <div className="fila-botones fila-botones--entre">
            {autorizado ? (
              <span className="nota-suave" role="status">
                Consumos autorizados para la propuesta. Si cambias algo, vuelve a tocar «Usar estos consumos».
              </span>
            ) : (
              <span className="nota-suave">Revisa la tabla; al autorizarla, la propuesta se calcula con estos meses.</span>
            )}
            <Boton variante="principal" icono={<Check aria-hidden />} onClick={alUsar}>
              Usar estos consumos
            </Boton>
          </div>
        </>
      )}
      {error && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
